import { useState, useEffect, useMemo } from 'react';
import { CARD_FRAMES_SOLID, CARD_FRAMES_TRANSPARENT } from '../utils/constants';
import type { CardAttribute } from '../types';

// ========================================
// カードイラスト検出フック
// ========================================

/** サポートする画像形式（優先順） */
const SUPPORTED_EXTENSIONS = ['webp', 'png'] as const;

/** カードイラストのベースパスを取得 */
const getCardIllustrationBase = (cardId: string): string => {
  return `/images/cards/${cardId}`;
};

interface UseCardIllustrationResult {
  /** イラストが存在するかどうか */
  hasIllustration: boolean;
  /** イラスト画像パス（見つかった形式のパス） */
  illustrationPath: string;
  /** 使用すべきフレーム画像パス（イラストの有無で自動切り替え） */
  frameImage: string;
  /** イラスト読み込み中かどうか */
  isLoading: boolean;
}

/**
 * カードイラストの存在を検出し、適切なフレーム画像を返すフック
 * WebP優先、PNGフォールバック
 * @param cardId カードID（undefined/nullの場合はデフォルト値を返す）
 * @param attribute カードの属性
 * @returns イラスト情報とフレーム画像パス
 */
export const useCardIllustration = (
  cardId: string | undefined | null,
  attribute: CardAttribute = 'なし'
): UseCardIllustrationResult => {
  const [hasIllustration, setHasIllustration] = useState(false);
  const [illustrationPath, setIllustrationPath] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!cardId) {
      setHasIllustration(false);
      setIllustrationPath('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const basePath = getCardIllustrationBase(cardId);
    let currentIndex = 0;
    let cancelled = false;

    const tryNextFormat = () => {
      if (cancelled) return;

      if (currentIndex >= SUPPORTED_EXTENSIONS.length) {
        // 全形式を試したが見つからなかった
        setHasIllustration(false);
        setIllustrationPath('');
        setIsLoading(false);
        return;
      }

      const ext = SUPPORTED_EXTENSIONS[currentIndex];
      const path = `${basePath}.${ext}`;
      const img = new Image();

      img.onload = () => {
        if (cancelled) return;
        // 見つかった
        setHasIllustration(true);
        setIllustrationPath(path);
        setIsLoading(false);
      };

      img.onerror = () => {
        if (cancelled) return;
        // 次の形式を試す
        currentIndex++;
        tryNextFormat();
      };

      img.src = path;
    };

    tryNextFormat();

    return () => {
      cancelled = true;
    };
  }, [cardId]);

  const frameImage = useMemo(() => {
    if (hasIllustration) {
      return CARD_FRAMES_TRANSPARENT[attribute] || CARD_FRAMES_TRANSPARENT['なし'];
    }
    return CARD_FRAMES_SOLID[attribute] || CARD_FRAMES_SOLID['なし'];
  }, [hasIllustration, attribute]);

  return {
    hasIllustration,
    illustrationPath,
    frameImage,
    isLoading,
  };
};

export default useCardIllustration;
